import React, { useEffect, useState, useRef, useCallback, useContext, memo } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import { useAtom } from 'jotai';
import axios from 'axios';
import config from '../../Config';
import { useDebounce } from 'use-debounce';
import CardModel from '../../Components/CardModel';
import Loading from '../../Components/Loading';
import ScrollToTopButton from '../../Components/ScrollToTopButton';
import DarkModeContext from '../../Components/DarkModeContext';
import { authUserAtom, dataUserAtom, visionUserModeAtom, modelsListAtom, modelsPageAtom, modelsHasMoreAtom, modelsSearchAtom, modelsScrollYAtom } from '../../Services/store/atoms';
import LayoutHomeUser from '../../Layouts/Admin/Layout';
import InputSearch from '../../Components/InputSearch';
import Spinner from '../../Components/Spinner';
import { FaSadTear } from "react-icons/fa";

const LandingWithoutHeader = memo((props) => {
  const history = useHistory();
  const location = useLocation();
  const [savedSearch, setSavedSearch] = useAtom(modelsSearchAtom);
  const [search, setSearch] = useState(savedSearch || '');
  const [value] = useDebounce(search, 1000);
  const [isLoading, setIsLoading] = useState(false);
  const [authUser] = useAtom(authUserAtom);
  const [dataUser] = useAtom(dataUserAtom);
  const [visionUserMode] = useAtom(visionUserModeAtom);
  const [models, setModels] = useAtom(modelsListAtom);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [currentPage, setCurrentPage] = useAtom(modelsPageAtom);
  const [hasMore, setHasMore] = useAtom(modelsHasMoreAtom);
  const [scrollY, setScrollY] = useAtom(modelsScrollYAtom);
  const { darkMode } = useContext(DarkModeContext);
  const observer = useRef();
  const fetched = useRef(false);
  const seed = useRef(Math.floor(Math.random() * 1000000));

  const lastModelRef = useCallback(
    (node) => {
      if (isLoading || !hasMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchModels(currentPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, currentPage]
  );

  const fetchModels = useCallback(async (page = 1) => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authUser?.token}`
      };
      
      const requestData = new URLSearchParams();
      if (value && value.trim() !== '') {
        requestData.append('search', value.trim());
      }
      
      if (dataUser?.role === 'admin' && visionUserMode) {
        requestData.append('view_as_user', '1');
      }
      requestData.append('view_as_user', '0');
      requestData.append('page', page);
      requestData.append('seed', seed.current);
      
      const response = await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/packs/listModel`, requestData, { headers });
      
      if (response.data && response.data.data) {
        setModels(prev => 
          page === 1 ? response.data.data : [...prev, ...response.data.data]
        );
        setHasMore(response.data.current_page < response.data.last_page);
        setCurrentPage(response.data.current_page);
        setNextPageUrl(response.data.next_page_url);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authUser?.token, value, isLoading, dataUser?.role, visionUserMode]);

  useEffect(() => {
    seed.current = Math.floor(Math.random() * 1000000);
    setCurrentPage(1);
    setModels([]);
    fetchModels(1);
    fetched.current = true;
  }, [value]);

  useEffect(() => {
    setSavedSearch(value);
  }, [value]);

  useEffect(() => {
    return () => setScrollY(window.scrollY);
  }, []);

  const handlerSearch = (value) => {
    setSearch(value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setCurrentPage(1);
      setModels([]);
      fetchModels(1);
    }
  };

  const clickModel = (userId) => {
    setScrollY(window.scrollY);
    setSavedSearch(value);
    const origin = location.state?.background || location;
    const base = origin.pathname.split('/')[1];
    const dashboards = ['model', 'client', 'admin', 'user', 'assistant'];
    if (dashboards.includes(base)) {
      history.push(`/${base}/profileModal/${userId}`, { background: origin });
    } else {
      history.push(`/profileModal/${userId}`, { background: origin });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <LayoutHomeUser>
      <div className="mb-6 max-w-md mx-auto">
        <InputSearch
          handlerSearch={handlerSearch}
          onKeyDown={handleKeyDown}
          darkMode={darkMode}
          placeholder="Search models..."
          size="md"
          showSubmitButton={false}
          hideCategories={true}
        />
      </div>
      
      {models.length > 0 ? (
        <div className='flex flex-wrap justify-center gap-4'>
          {models.map((model, index) => (
            <div
              key={model.user_id}
              ref={index === models.length - 1 ? lastModelRef : null}
              className="cursor-pointer"
              onClick={() => clickModel(model.user_id)}
            >
              <CardModel model={model} index={index} />
            </div>
          ))}
        </div>
      ) : (
        !isLoading && (
          <div className="flex flex-col items-center mt-16">
            <FaSadTear className={`text-4xl ${darkMode ? 'text-white' : 'text-black'}`} />
            <p className={`p-4 ${darkMode ? 'text-white' : 'text-gray-500'}`}>No hay modelos para mostrar</p>
          </div>
        )
      )}

{isLoading && <Spinner />}
      <ScrollToTopButton onClick={scrollToTop} />
    </LayoutHomeUser>
  );
});

export default LandingWithoutHeader;